import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { EDIT_QUESTIONS } from '@/modules/projects/components/Workbench/editRequests'
import { PendingNote } from '@/modules/projects/components/Workbench/PendingNote'
import type { PendingEdits } from '@/modules/projects/components/Workbench/useConfirmedEdits'
import { EditPreviewBody } from './EditPreviewBody'
import { fileLine } from './preview'

// No rules inside the dialog: space separates everything. The dialog is white so the fills read.

interface ConfirmEditDialogProps {
  pending: PendingEdits | null
  saving: boolean
  // A project without reconciliation shows no verdicts anywhere, so the dialog does not either.
  showsVerdict: boolean
  onConfirm: () => void
  onCancel: () => void
}

export const ConfirmEditDialog = ({
  pending,
  saving,
  showsVerdict,
  onConfirm,
  onCancel,
}: ConfirmEditDialogProps) => {
  const kind = pending?.requests[0]?.kind ?? 'edit_account'
  const question = EDIT_QUESTIONS[kind]
  const previews = pending?.previews ?? []
  const subject = previews.length === 1 ? previews[0].subject : null
  const warned = previews.some((preview) => preview.warnings.length > 0)
  const undoable = previews.every((preview) => preview.undoable)

  return (
    <Dialog
      open={pending !== null}
      onOpenChange={(open) => !open && onCancel()}
    >
      <DialogContent data-confirm-edit className="max-w-lg gap-5 bg-white">
        <DialogHeader>
          <DialogTitle className="text-lg">{question.title}</DialogTitle>
          {subject ? (
            <DialogDescription asChild>
              <div>
                <p className="text-sm font-medium text-neutral-950">
                  {subject.period}
                </p>
                <p className="truncate text-xs text-neutral-600">
                  {fileLine(subject)}
                </p>
              </div>
            </DialogDescription>
          ) : (
            <DialogDescription>
              Nothing changes until you confirm.
            </DialogDescription>
          )}
        </DialogHeader>

        {pending && !pending.previews && !pending.problem ? (
          <PendingNote text="Working out what this changes…" />
        ) : null}

        <div className="max-h-[60vh] space-y-8 overflow-y-auto">
          {previews.map((preview) => (
            <EditPreviewBody
              key={preview.summary}
              preview={preview}
              showsVerdict={showsVerdict}
              showsSubject={previews.length > 1}
            />
          ))}
        </div>

        {pending?.problem ? (
          <p data-edit-problem className="text-sm text-red-700">
            {pending.problem}
          </p>
        ) : null}

        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-neutral-600">
            {undoable
              ? 'You can undo this from History.'
              : 'This cannot be undone.'}
          </p>
          <div className="flex gap-2">
            <Button theme="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              theme={kind === 'remove_account' ? 'danger' : 'default'}
              disabled={previews.length === 0 || saving}
              onClick={onConfirm}
            >
              {warned ? `${question.confirm} anyway` : question.confirm}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
